import type { UseLazyExportJobDetailQuery } from './components/ExportJobDetailDrawer';
import { childrenBusinessPageBreadcrumbs } from '../../config/breadcrumbs';
import type { GetExportJobsParams } from './services/exportJobs.service';
import type { NavigateLink } from '@/components/navigate/NavigateLinks';
import ExportJobDetailDrawer from './components/ExportJobDetailDrawer';
import { useGetExportJobsQuery } from './services/exportJobs.service';
import ExportJobsFiltersBar from './components/ExportJobsFilters';
import { useExportJobsPage } from './hooks/useExportJobsPage';
import ExportJobsTable from './components/ExportJobsTable';
import { Info as InfoIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { NavigateLinks } from '@/components';
import { useState, useMemo } from 'react';
import { Alert } from '@mui/material';
import type {
  ExportJobListResponse,
  ExportJobsFilters,
  ExportKind,
} from './models/ExportJob';

export type UseExportJobsQuery = (args: GetExportJobsParams) => {
  data?: ExportJobListResponse;
  isFetching: boolean;
};

export interface JobsPageProps {
  breadcrumbs?: NavigateLink[];
  /** Si se pasa, el filtro de tipo solo ofrece estos kinds. */
  allowedKinds?: ExportKind[];
  infoMessage?: string;
  useJobsQuery?: UseExportJobsQuery;
  useLazyJobDetailQuery?: UseLazyExportJobDetailQuery;
}

const DEFAULT_INFO_MESSAGE =
  'Se muestran los jobs de exportación de los últimos 7 días. Solo ves tus propios jobs.';

const Jobs = ({
  breadcrumbs: breadcrumbsProp,
  allowedKinds,
  infoMessage = DEFAULT_INFO_MESSAGE,
  useJobsQuery = useGetExportJobsQuery,
  useLazyJobDetailQuery,
}: JobsPageProps) => {
  const navigate = useNavigate();
  const breadcrumbs = useMemo(
    () => breadcrumbsProp ?? childrenBusinessPageBreadcrumbs(navigate),
    [breadcrumbsProp, navigate]
  );
  const { page, pageSize, filters, setPage, applyFilters } = useExportJobsPage();
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const { data, isFetching } = useJobsQuery({ filters, page, pageSize });

  const handleSearch = (newFilters: ExportJobsFilters) => {
    applyFilters(newFilters);
  };

  return (
    <section className='flex flex-col gap-3 w-full h-full min-h-0'>
      <NavigateLinks actualPage='Mis archivos' breadcrumbs={breadcrumbs} />

      <Alert
        severity='info'
        icon={<InfoIcon fontSize='small' />}
        sx={{ borderRadius: 2 }}
      >
        {infoMessage}
      </Alert>

      <ExportJobsFiltersBar onSearch={handleSearch} allowedKinds={allowedKinds} />

      <ExportJobsTable
        jobs={data?.rows ?? []}
        isLoading={isFetching}
        totalItems={data?.count ?? 0}
        currentPage={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onViewDetail={setSelectedJobId}
      />

      <ExportJobDetailDrawer
        jobId={selectedJobId}
        onClose={() => setSelectedJobId(null)}
        useLazyDetailQuery={useLazyJobDetailQuery}
      />
    </section>
  );
};

export default Jobs;
